import { Theme, alpha } from '@mui/material/styles';
import { RADIUS, REDUCED_MOTION } from './tokens';

// Shared interaction/motion recipes, so feature folders don't re-derive their own focus
// rings, gradients and entrances.

// Matches the focused-field ring, so tabbing has one consistent signal. `color` is for surfaces with their own accent.
export const focusRingSx = (theme: Theme, color: string = theme.palette.primary.main) => ({
  '&.Mui-focusVisible': { outline: 'none', boxShadow: `0 0 0 3px ${alpha(color, 0.35)}` }
});

// The everyday primary: a flat brand fill that darkens on hover. This is what an
// ordinary action (Save, Confirm, Apply) looks like. `ctaSx` below is NOT that —
// its gradient and glow read as "buy", so on a Save button the page looks like it
// is selling something, and repeated down a list it stops meaning anything.
export const primarySx = (theme: Theme) => {
  const p = theme.palette.primary;
  return {
    ...focusRingSx(theme),
    borderRadius: RADIUS.control,
    px: 2.5,
    fontWeight: 600,
    letterSpacing: '0.01em',
    color: p.contrastText,
    backgroundColor: p.main,
    backgroundImage: 'none',
    boxShadow: 'none',
    transition: 'background-color .18s ease',
    // The text colour is pinned: the theme's text-variant hover would turn it primary.dark, unreadable on this fill.
    '&:hover': { backgroundColor: p.dark, color: p.contrastText, boxShadow: 'none' },
    '&.Mui-disabled': { backgroundColor: alpha(p.main, 0.35), color: p.contrastText },
    [REDUCED_MOTION]: { transition: 'none' }
  } as const;
};

// A row-scoped action: brand-tinted, not filled. One of these per row stays quiet;
// a filled button per row would give a list of them the weight of a page CTA.
export const quietActionSx = (theme: Theme) => {
  const p = theme.palette.primary;
  return {
    ...focusRingSx(theme),
    borderRadius: RADIUS.control,
    px: 2.5,
    fontWeight: 600,
    color: p.main,
    backgroundColor: alpha(p.main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
    backgroundImage: 'none',
    border: `1px solid ${alpha(p.main, 0.35)}`,
    boxShadow: 'none',
    transition: 'background-color .18s ease, border-color .18s ease',
    '&:hover': {
      backgroundColor: alpha(p.main, theme.palette.mode === 'dark' ? 0.28 : 0.18),
      borderColor: p.main,
      boxShadow: 'none'
    },
    '&.Mui-disabled': { color: alpha(p.main, 0.4), borderColor: alpha(p.main, 0.15), backgroundColor: 'transparent' },
    [REDUCED_MOTION]: { transition: 'none' }
  } as const;
};

// The billing tables' row action ("Purchase"): a soft tint of its colour, a hairline border,
// deeper on hover and press. Same 8px corner as the drawer's ink button.
export const softButtonSx = (theme: Theme, color: 'primary' | 'error' | 'secondary' = 'primary') => {
  const c = theme.palette[color];
  const dark = theme.palette.mode === 'dark';
  return {
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: 'none',
    background: alpha(c.main, dark ? 0.2 : 0.12),
    border: `1px solid ${alpha(c.main, dark ? 0.4 : 0.24)}`,
    color: dark ? c.light : c.dark,
    transition: 'background-color 150ms ease, border-color 150ms ease',
    '&:hover': { background: alpha(c.main, dark ? 0.28 : 0.18), borderColor: alpha(c.main, dark ? 0.5 : 0.35), boxShadow: 'none' },
    '&:active': { background: alpha(c.main, dark ? 0.32 : 0.22) }
  } as const;
};

// Button icons that answer the pointer: a leading icon grows a touch, a trailing arrow nudges on.
export const buttonIconMotionSx = {
  '& .MuiButton-startIcon, & .MuiButton-endIcon': { transition: 'transform .2s cubic-bezier(.2,.7,.3,1)' },
  '&:hover .MuiButton-startIcon': { transform: 'scale(1.12)' },
  '&:hover .MuiButton-endIcon': { transform: 'translateX(3px)' },
  [REDUCED_MOTION]: { '&:hover .MuiButton-startIcon, &:hover .MuiButton-endIcon': { transform: 'none' } }
} as const;

// PURCHASE ONLY — gradient, glow, a sheen that sweeps on hover and a small lift.
// Reserve it for surfaces that actually sell something (Buy Room, MCP). Everything
// else wants `primarySx`. Under reduced motion the glow carries it alone.
export const ctaSx = (theme: Theme) => {
  const p = theme.palette.primary;
  return {
    ...focusRingSx(theme),
    position: 'relative',
    overflow: 'hidden',
    borderRadius: RADIUS.control,
    px: 3,
    fontWeight: 700,
    letterSpacing: '0.01em',
    color: p.contrastText,
    background: `linear-gradient(135deg, ${p.dark} 0%, ${p.main} 55%, ${p.light} 130%)`,
    boxShadow: `0 8px 24px -8px ${alpha(p.main, 0.55)}, inset 0 1px 0 ${alpha('#fff', 0.25)}`,
    transition: 'transform .18s ease, box-shadow .18s ease',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: `linear-gradient(110deg, transparent 30%, ${alpha('#fff', 0.28)} 50%, transparent 70%)`,
      transform: 'translateX(-120%)',
      pointerEvents: 'none'
    },
    '&:hover': {
      background: `linear-gradient(135deg, ${p.dark} 0%, ${p.main} 45%, ${p.light} 115%)`,
      boxShadow: `0 12px 32px -8px ${alpha(p.main, 0.65)}, inset 0 1px 0 ${alpha('#fff', 0.3)}`,
      transform: 'translateY(-1px)'
    },
    '&:hover::after': { transition: 'transform .6s ease', transform: 'translateX(120%)' },
    '&:active': { transform: 'translateY(0)' },
    [REDUCED_MOTION]: {
      transition: 'none',
      '&::after': { display: 'none' },
      '&:hover': { transform: 'none' }
    }
  } as const;
};

// Staggered section entrance; `i` is the section index.
export const riseIn = (
  i: number,
  { distance = 6, step = 70, duration = 0.4 }: { distance?: number; step?: number; duration?: number } = {}
) => ({
  '@keyframes uiRise': { from: { opacity: 0, transform: `translateY(${distance}px)` }, to: { opacity: 1, transform: 'none' } },
  animation: `uiRise ${duration}s cubic-bezier(.2,.7,.3,1) ${i * step}ms backwards`,
  [REDUCED_MOTION]: { animation: 'none' }
});

// A chosen card or option: brand hairline, faint tint and a soft ring, so "picked"
// reads the same in every selector.
export const selectedSurfaceSx = (theme: Theme) => {
  const p = theme.palette.primary.main;
  return {
    borderColor: alpha(p, 0.6),
    backgroundColor: alpha(p, theme.palette.mode === 'dark' ? 0.08 : 0.04),
    boxShadow: `0 0 0 3px ${alpha(p, 0.12)}`
  } as const;
};

// The depth half of a clickable surface. Deliberately no transform: callers that want a
// physical rise supply it themselves (framer springs), so the two never fight.
export const hoverLiftSx = (theme: Theme) => ({
  transition: 'box-shadow .18s ease, border-color .18s ease',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.5),
    boxShadow: `0 18px 38px -22px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.75 : 0.45)}`
  },
  '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  [REDUCED_MOTION]: { transition: 'none' }
});
